import { useState } from 'react';
import { FileText, CheckCircle, Loader2, Search, Mail } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface DonationInfo {
  id: number;
  donation_num: string;
  amount: number;
  donor_name: string;
  donor_phone: string;
  donor_email: string | null;
  created_at: string;
}

export default function ReceiptRequest() {
  const [mode, setMode] = useState<'search' | 'form' | 'submitted'>('search');
  const [searchNum, setSearchNum] = useState('');
  const [donation, setDonation] = useState<DonationInfo | null>(null);
  const [searching, setSearching] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);

  const [form, setForm] = useState({
    donorName: '',
    donorEmail: '',
    idCardLast4: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async () => {
    if (!searchNum.trim()) return;
    setSearching(true);
    setAlreadyRequested(false);
    const res = await apiFetch<DonationInfo>(`/api/public/receipt-search?num=${encodeURIComponent(searchNum.trim())}`);
    setSearching(false);
    if (res.success && res.data) {
      setDonation(res.data);
      setForm({
        donorName: res.data.donor_name || '',
        donorEmail: res.data.donor_email || '',
        idCardLast4: '',
      });
      setMode('form');
    } else {
      if (res.error?.code === 'ALREADY_REQUESTED') {
        setAlreadyRequested(true);
      } else {
        alert(res.error?.message || '找不到捐款記錄');
      }
    }
  };

  const handleSubmit = async () => {
    if (!donation) return;
    if (!form.donorName.trim()) {
      alert('請輸入姓名（與收據抬頭一致）');
      return;
    }
    setSubmitting(true);
    const res = await apiFetch<{ requestNum: string; message: string }>('/api/public/receipt-request', {
      method: 'POST',
      body: JSON.stringify({
        donationNum: donation.donation_num,
        donorName: form.donorName,
        donorPhone: donation.donor_phone || '',
        donorEmail: form.donorEmail,
        idCardLast4: form.idCardLast4,
        mailingAddress: '',
      }),
    });
    setSubmitting(false);
    if (res.success) {
      setMode('submitted');
    } else {
      alert(res.error?.message || '提交失敗');
    }
  };

  if (mode === 'submitted') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">申請已收到</h2>
          <p className="text-gray-600 mb-4">我們將在 14 個工作天內以電子方式發送收據</p>
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-2">
            <Mail className="w-4 h-4" />
            <span>收據將通過 WhatsApp 或電郵發送</span>
          </div>
          <p className="text-sm text-gray-500">如有問題請 WhatsApp 聯絡我們</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <FileText className="w-12 h-12 text-brand-600 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">扣稅收據申請</h1>
          <p className="text-gray-600 text-sm mt-1">填寫資料，我們會以電子方式發送正式收據給您</p>
        </div>

        {mode === 'search' && (
          <div className="bg-white rounded-3xl shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">輸入捐款編號</label>
            <div className="flex gap-2">
              <input type="text" value={searchNum} onChange={(e) => setSearchNum(e.target.value)} className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="例如：DN-XXXXXX" />
              <button onClick={handleSearch} disabled={searching} className="btn-primary px-5 disabled:opacity-50">
                {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              </button>
            </div>

            {alreadyRequested && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                <CheckCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-amber-800">電子收據已經完成提交</p>
                <p className="text-xs text-amber-600 mt-1">此捐款編號的收據申請已記錄在案，無需重複申請</p>
              </div>
            )}
          </div>
        )}

        {mode === 'form' && donation && (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">捐款資料</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs">編號</p>
                  <p className="font-mono font-medium">{donation.donation_num}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">金額</p>
                  <p className="font-medium">HK$ {(donation.amount / 100).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">日期</p>
                  <p className="font-medium">{donation.created_at.split('T')[0]}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">捐款人</p>
                  <p className="font-medium">{donation.donor_name}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-6 space-y-4">
              <h3 className="font-bold text-gray-900">收據資料</h3>
              <p className="text-xs text-gray-500 -mt-2">收據將以電子方式（WhatsApp / 電郵 / PDF）發送，無需郵寄</p>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">姓名（與收據抬頭一致）*</label>
                <input type="text" value={form.donorName} onChange={(e) => setForm({ ...form, donorName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="姓名或公司名" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">電郵地址</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" value={form.donorEmail} onChange={(e) => setForm({ ...form, donorEmail: e.target.value })} className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">身份證尾四碼（稅務局核對用）</label>
                <input type="text" maxLength={4} value={form.idCardLast4} onChange={(e) => setForm({ ...form, idCardLast4: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="例如：1234" />
              </div>
            </div>

            <button onClick={handleSubmit} disabled={submitting} className="w-full flex items-center justify-center gap-2 btn-primary py-3.5 disabled:opacity-50">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              確認申請電子收據
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
